'use client';

import { useMemo, useState } from 'react';
import { BodyDefault, MetaDefault } from '../../../../components/typography';
import { Combobox } from '../../../../components/combobox';
import { createListCollection } from '../../../../utils';
import { IconButton } from '../../../../components/icon-button';
import { Input } from '../../../../components/input';
import { Box, VStack } from '@terra/ui-styled-system';
import { ArrowUpDownIcon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { LinkableWebApplication } from './web-app-types';

export type WebAppClusterLinkSelectProps = {
  applications: LinkableWebApplication[];
  value: string;
  onValueChange: (value: string) => void;
};

export function WebAppClusterLinkSelect({
  applications,
  value,
  onValueChange,
}: WebAppClusterLinkSelectProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const selectedApplication = applications.find(
    (application) => application.id === value,
  );
  const selectedLabel = selectedApplication?.name ?? '';

  const filteredApplications = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const matchingApplications = normalizedSearch
      ? applications.filter(
          (application) =>
            application.name.toLowerCase().includes(normalizedSearch) ||
            application.url.toLowerCase().includes(normalizedSearch),
        )
      : applications;

    const items = matchingApplications.map((application) => ({
      value: application.id,
      label: application.name,
      url: application.url,
    }));

    if (
      selectedApplication &&
      !items.some((item) => item.value === selectedApplication.id)
    ) {
      return [
        {
          value: selectedApplication.id,
          label: selectedApplication.name,
          url: selectedApplication.url,
        },
        ...items,
      ];
    }

    return items;
  }, [applications, searchValue, selectedApplication]);

  const collection = useMemo(
    () =>
      createListCollection({
        items: filteredApplications,
        itemToValue: (item) => item.value,
        itemToString: (item) => item.label,
      }),
    [filteredApplications],
  );

  const inputValue = searchValue || (isOpen ? '' : selectedLabel);

  return (
    <Box
      w="full"
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <Combobox.Root
        collection={collection}
        value={value ? [value] : []}
        inputValue={inputValue}
        onValueChange={(details) => {
          const nextValue = details.value[0];

          if (nextValue) {
            onValueChange(nextValue);
          }

          setSearchValue('');
        }}
        onInputValueChange={(details) => {
          if (!isOpen) {
            setSearchValue('');

            return;
          }

          setSearchValue(details.inputValue);
        }}
        onOpenChange={(details) => {
          setIsOpen(details.open);

          if (!details.open) {
            setSearchValue('');
          }
        }}
        positioning={{ strategy: 'fixed' }}
        width="full"
        openOnClick
        closeOnSelect
      >
        <Combobox.Control h="40px">
          <Combobox.Input placeholder={t('linkExisting')} asChild>
            <Input size="md" pe="10" />
          </Combobox.Input>
          <Combobox.Trigger asChild>
            <IconButton variant="link" size="xs" aria-label={t('linkExisting')}>
              <ArrowUpDownIcon size={16} />
            </IconButton>
          </Combobox.Trigger>
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content maxHeight="280px" overflowY="auto">
            {collection.items.length === 0 ? (
              <Box px={3} py={3}>
                <MetaDefault color="fg.muted">
                  {applications.length === 0
                    ? t('linkExisting')
                    : tShared('noResultFound')}
                </MetaDefault>
              </Box>
            ) : (
              <Combobox.ItemGroup>
                {collection.items.map((item) => (
                  <Combobox.Item
                    key={item.value}
                    item={item}
                    height="auto"
                    minH="52px"
                    py={2}
                    alignItems="center"
                  >
                    <VStack
                      flex="1"
                      gap={0}
                      alignItems="flex-start"
                      overflow="hidden"
                      py={1}
                    >
                      <Combobox.ItemText asChild>
                        <BodyDefault fontWeight="medium" truncate w="full">
                          {item.label}
                        </BodyDefault>
                      </Combobox.ItemText>
                      {item.url ? (
                        <MetaDefault color="fg.muted" truncate w="full">
                          {item.url}
                        </MetaDefault>
                      ) : null}
                    </VStack>
                    <Combobox.ItemIndicator>
                      <Tick01Icon size={14} />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                ))}
              </Combobox.ItemGroup>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
    </Box>
  );
}
